import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSequence } from '../context/SequenceContext';
import SafeIcon from '../common/SafeIcon';
import { motion } from 'framer-motion';

const SequenceImporter = () => {
  const navigate = useNavigate();
  const { createImportJob, runImportJob, importJobs } = useSequence();
  
  const [step, setStep] = useState(1);
  const [csvText, setCsvText] = useState(`sequence_name,project_id,channel,step_no,delay_days,step_type,template_name,variant,attach_tracked_link,auto_generate_draft
HMS Varsling – Standard,proj_hmsvarsling,Email,1,0,Email,Intro – Møte,Normal,true,true
HMS Varsling – Standard,proj_hmsvarsling,Email,2,3,Email,Oppfølging 1,Kort,true,true
HMS Varsling – Standard,proj_hmsvarsling,Email,3,7,Email,Breakup,Direkte,false,true`);
  
  const [options, setOptions] = useState({
    mode: 'Upsert',
    create_missing_templates: true,
    overwrite_existing_steps: true
  });

  const [currentJob, setCurrentJob] = useState(null);

  const handleValidate = async () => {
    const job = await createImportJob(csvText, options);
    setCurrentJob(job);
    setStep(2);
  };

  const handleExecute = async () => {
    if (!currentJob) return;
    await runImportJob(currentJob.job_id);
    setStep(3);
  };

  return (
    <div className="p-8 max-w-7xl mx-auto min-h-screen space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center border-b border-slate-200 pb-6">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">Sequence Importer V1</h1>
          <p className="text-slate-500 font-medium mt-1">Importer sekvenser og maler fra CSV.</p>
        </div>
        <button onClick={() => navigate('/sequences')} className="text-slate-500 hover:text-slate-700 font-bold text-sm">
          Avbryt
        </button>
      </div>

      {/* Progress */}
      <div className="flex gap-4 mb-8">
        {[1, 2, 3].map(s => (
          <div key={s} className={`flex-1 h-2 rounded-full ${step >= s ? 'bg-primary-600' : 'bg-slate-200'}`} />
        ))}
      </div>

      {/* STEP 1: INPUT */}
      {step === 1 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 animate-in fade-in slide-in-from-left-4">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm">
              <label className="block text-sm font-black text-slate-900 uppercase tracking-widest mb-4">
                Lim inn CSV data
              </label>
              <textarea 
                value={csvText}
                onChange={(e) => setCsvText(e.target.value)}
                className="w-full h-[400px] p-4 bg-slate-900 text-slate-200 font-mono text-xs rounded-xl focus:ring-2 focus:ring-primary-500 outline-none resize-none"
                placeholder="sequence_name,project_id,..."
              />
            </div>
          </div>
          
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm space-y-4">
              <h3 className="font-black text-slate-900">Innstillinger</h3>
              
              <div className="space-y-3">
                <label className="flex items-center justify-between p-3 border rounded-xl cursor-pointer hover:bg-slate-50">
                  <span className="text-sm font-bold text-slate-700">Opprett manglende maler</span>
                  <input type="checkbox" checked={options.create_missing_templates} onChange={e => setOptions({...options, create_missing_templates: e.target.checked})} className="w-5 h-5 rounded text-primary-600" />
                </label>
                
                <label className="flex items-center justify-between p-3 border rounded-xl cursor-pointer hover:bg-slate-50">
                  <span className="text-sm font-bold text-slate-700">Overskriv eksisterende steg</span>
                  <input type="checkbox" checked={options.overwrite_existing_steps} onChange={e => setOptions({...options, overwrite_existing_steps: e.target.checked})} className="w-5 h-5 rounded text-primary-600" />
                </label>
              </div>

              <div className="pt-4 border-t border-slate-100">
                <button onClick={handleValidate} disabled={!csvText} className="w-full py-3 bg-slate-900 text-white rounded-xl font-black text-xs uppercase tracking-widest hover:bg-slate-800 disabled:opacity-50">
                  Valider CSV
                </button>
              </div>
            </div>

            <div className="p-4 bg-blue-50 border border-blue-100 rounded-2xl text-xs text-blue-800">
              <p className="font-bold mb-2"><SafeIcon icon="ℹ️" /> Formatkrav:</p>
              <ul className="list-disc pl-4 space-y-1">
                <li>sequence_name (Påkrevd)</li>
                <li>project_id (Påkrevd)</li>
                <li>step_no (Tall)</li>
                <li>template_name (Match/Opprett)</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* STEP 2: VALIDATION */}
      {step === 2 && currentJob && (
        <div className="space-y-6 animate-in fade-in slide-in-from-right-4">
          <div className="flex justify-between items-center bg-white p-6 rounded-[2rem] border border-slate-200">
            <div className="flex gap-8">
              <div>
                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Totalt rader</p>
                <p className="text-2xl font-black text-slate-900">{currentJob.summary.rows_total}</p>
              </div>
              <div>
                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Gyldige</p>
                <p className="text-2xl font-black text-emerald-600">{currentJob.summary.rows_valid}</p>
              </div>
              <div>
                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Feil</p>
                <p className="text-2xl font-black text-rose-600">{currentJob.summary.rows_invalid}</p>
              </div>
            </div>
            
            <button 
              onClick={handleExecute} 
              disabled={currentJob.summary.rows_invalid > 0} 
              className="px-8 py-3 bg-primary-600 text-white rounded-xl font-black text-xs uppercase tracking-widest hover:bg-primary-700 disabled:bg-slate-300 shadow-lg shadow-primary-200"
            >
              Start Import 🚀
            </button>
          </div>

          <div className="bg-white border border-slate-200 rounded-[2rem] overflow-hidden">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>
                  <th className="px-6 py-4 font-black text-slate-400 uppercase tracking-widest text-[10px]">Rad</th>
                  <th className="px-6 py-4 font-black text-slate-400 uppercase tracking-widest text-[10px]">Sekvens</th>
                  <th className="px-6 py-4 font-black text-slate-400 uppercase tracking-widest text-[10px]">Steg</th>
                  <th className="px-6 py-4 font-black text-slate-400 uppercase tracking-widest text-[10px]">Mal</th>
                  <th className="px-6 py-4 font-black text-slate-400 uppercase tracking-widest text-[10px]">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {currentJob.rows.map((row, i) => (
                  <tr key={i} className={row.validation_status === 'FEIL' ? 'bg-rose-50' : 'hover:bg-slate-50'}>
                    <td className="px-6 py-4 font-mono text-slate-500">{row.row_no}</td>
                    <td className="px-6 py-4 font-bold text-slate-900">{row.sequence_name}</td>
                    <td className="px-6 py-4">#{row.step_no} ({row.step_type})</td>
                    <td className="px-6 py-4">{row.template_name}</td>
                    <td className="px-6 py-4">
                      {row.validation_status === 'OK' ? (
                        <span className="inline-flex items-center gap-1 text-emerald-600 font-bold text-xs"><SafeIcon icon="✅" /> OK</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-rose-600 font-bold text-xs" title={row.validation_errors}><SafeIcon icon="❌" /> {row.validation_errors}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {currentJob.summary.rows_invalid > 0 && (
            <div className="text-center">
              <button onClick={() => setStep(1)} className="text-slate-500 font-bold hover:underline">Gå tilbake og rett feil i CSV</button>
            </div>
          )}
        </div>
      )}

      {/* STEP 3: RESULT */}
      {step === 3 && currentJob && (
        <div className="flex flex-col items-center justify-center py-20 animate-in zoom-in duration-300">
          <div className="w-24 h-24 bg-emerald-100 rounded-full flex items-center justify-center mb-8 shadow-xl shadow-emerald-100">
            <SafeIcon icon="🎉" className="w-12 h-12 text-emerald-600" />
          </div>
          <h2 className="text-3xl font-black text-slate-900 mb-2">Import Fullført!</h2>
          <p className="text-slate-500 font-medium mb-8">
            Vi har opprettet eller oppdatert sekvensene dine.
          </p>
          
          <div className="flex gap-4">
            <button onClick={() => navigate('/sequences')} className="px-8 py-3 bg-slate-900 text-white rounded-xl font-bold hover:bg-slate-800 shadow-lg">
              Gå til Sekvenser
            </button>
            <button onClick={() => { setStep(1); setCsvText(''); }} className="px-8 py-3 bg-white border border-slate-200 text-slate-700 rounded-xl font-bold hover:bg-slate-50">
              Ny Import
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SequenceImporter;